'use client';

import React, { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { Restaurant } from '@/lib/types/customer/RestaurantSearch';
import { Location, Star } from '@ui/Icons';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import If from '@ui/If';
import { storeFront } from '@/lib/utils/routeLinks';
import useAppLocale from '@/hooks/useAppLocale';
import { motion, useInView } from 'framer-motion';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useFavoriteHandler } from '@/hooks/useFavoriteHandler';
import { STOREFRONT } from '@/lib/utils/translation';
import FavoriteLoginModal from '@/components/FavoriteLoginModal';

interface SearchCardProps {
  restaurant: Restaurant;
  type?: 'delivery' | 'pickup' | 'all';
  isSimilarCard?: boolean;
}

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const SearchCard = ({
  restaurant,
  type,
  isSimilarCard = false,
}: SearchCardProps) => {
  const { lang } = useAppLocale();
  const T_COMMON = useTranslations('');
  const T_PROFILE = useTranslations('storefront.store_profile');
  const isOnlineShop = restaurant.type === 'online_shop';
  const coverUrl = restaurant.media?.cover?.[0].thumb_url ?? '/heroBurger.png';
  const logoUrl = restaurant.media?.logo?.[0].thumb_url;
  const {
    slug,
    name,
    address,
    short_description,
    avg_total_rating: rating,
    // restaurant_config: config,
  } = restaurant;

  const greyCardBg =
    restaurant?.features?.ui_storefront_use_gray_background_in_list ?? true;
  // const restaurantAllowsDelivery =
  //   (restaurant.allows_delivery && restaurant?.features?.handle_orders) ||
  //   false;

  const { is_open: isOpened, is_opening_soon: isOpeningSoon } = restaurant;

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '0px 0px -100px 0px' });

  const {
    isFavorite,
    toggleFavorite,
    isFavoriteToggleLoading,
    showLoginModal,
    setShowLoginModal,
    redirectToLogin,
  } = useFavoriteHandler('restaurant', restaurant.id);

  return (
    <>
      <motion.div
        ref={ref}
        variants={cardVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className={cn(
          'group flex w-full overflow-hidden rounded-[10px] border border-[#E5E7EB] bg-white transition-shadow duration-500',
          greyCardBg && 'grayscale',
          isSimilarCard
            ? 'm-4 hover:shadow-lg'
            : 'hover:shadow-[0px_20px_60px_rgba(0,0,0,0.15)]'
        )}
      >
        <Link
          href={`${storeFront?.restaurants[lang as 'de' | 'en']()}/` + slug}
          target="_blank"
          aria-label={T_COMMON('aria.go_to_this_restaurant', {
            restaurant: name,
          })}
          className={cn('flex w-full flex-col')}
        >
          <div className="relative">
            <div
              className="h-[200px] max-h-[190px] overflow-hidden"
              style={{
                backgroundImage: `url(${coverUrl})`,
                objectFit: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}
            ></div>

            {logoUrl && (
              <div className=" absolute bottom-[-21px] left-[25px] h-[56px] w-[56px] overflow-hidden rounded-[5px] border border-[#E5E7EB]">
                <Image
                  src={logoUrl}
                  alt={`${name} brand logo`}
                  className="object-cover"
                  fill
                  sizes="100vw"
                />
              </div>
            )}
          </div>
          <div className="flex flex-grow flex-col rounded-b-[10px] px-[25PX] pb-[20px] pt-[35PX] transition-all duration-500 group-hover:border-transparent">
            <div className="flex items-center justify-between">
              <h2 className="truncate text-h4 font-[700] text-title">{name}</h2>
              <button
                onClick={toggleFavorite}
                disabled={isFavoriteToggleLoading}
                aria-label={
                  isFavorite
                    ? T_COMMON(`${STOREFRONT.FAVORITES}.remove_from_favorite`)
                    : T_COMMON(`${STOREFRONT.FAVORITES}.add_to_favorite`)
                }
              >
                {isFavorite ? (
                  <HeartIconSolid className="h-6 w-6 text-primary" />
                ) : (
                  <HeartIcon className="h-6 w-6 text-gray-400" />
                )}
              </button>
            </div>
            {rating.counts.all > 0 && (
              <div className="mr-auto flex items-center">
                <Star />
                <span className="mx-[8px] inline-block text-h6 font-[700] text-[#646464]">
                  {rating.formatted.all}
                </span>
                <span>({rating.counts.all})</span>
              </div>
            )}
            {((type && type === 'pickup') ||
              restaurant.type === 'online_shop') &&
              short_description && (
                <p className="mt-[10px] truncate text-sm text-paragraph">
                  {short_description}
                </p>
              )}
            <If condition={restaurant.type !== 'online_shop'}>
              <div className="mt-[15px] flex items-center text-[#A5A5AA]">
                <div className="w-[23px]">
                  <Location />
                </div>
                {!!address && (
                  <span className="ml-[6px] block text-paragraph">
                    {address.street_name} {address.house_number},{' '}
                    {address.zip_code} {address.city}
                  </span>
                )}
              </div>
            </If>

            {/* <If condition={restaurantAllowsDelivery}>
            {type && type !== 'pickup' && (
              <>
                <div className="mt-[10px] flex items-center">
                  <If condition={config.minimum_order_value_for_delivery > 0}>
                    <div className="mr-[16px] flex items-center text-[#A5A5AA]">
                      <Cart />
                      <span className="ml-[6px] text-sm text-paragraph">
                        Min.{' '}
                        {
                          config.formatted_currency
                            .minimum_order_value_for_delivery
                        }
                      </span>
                    </div>
                  </If>

                  <div className="flex items-center text-[#A5A5AA]">
                    <DeliveryMini />
                    <span className="ml-[6px] text-sm text-paragraph">
                      {config.allows_free_delivery || config.delivery_fee === 0
                        ? T_COMMON('free')
                        : config.formatted_currency.delivery_fee}
                    </span>
                  </div>
                </div>
                <div className="mt-[10px] flex items-center space-x-3">
                  <If
                    condition={
                      config.minimum_delivery_time_in_minutes > 0 &&
                      config.maximum_delivery_time_in_minutes > 0
                    }
                  >
                    <div className="flex items-center text-[#A5A5AA]">
                      <Time />
                      <span className="ml-[6px] block text-paragraph">
                        {config.minimum_delivery_time_in_minutes}-
                        {config.maximum_delivery_time_in_minutes} min
                      </span>
                    </div>
                  </If>
                  
                </div>
              </>
            )}
          </If> */}

            {!isOnlineShop && (
              <span
                className={cn(
                  'mr-auto mt-[10px] rounded-full border px-[10px] py-[6px] text-xs uppercase leading-3',
                  // rating.counts.all > 0 ? '' : 'self-end',
                  isOpened &&
                    'border-[rgba(126,226,184,0.6)] bg-[#24C03014] text-[#007F5F]',
                  !isOpened &&
                    isOpeningSoon &&
                    'border-[#f5cd47] bg-[#f5cd471a] text-[#f2a33a]',
                  !isOpened &&
                    !isOpeningSoon &&
                    'border-[#ef000014] bg-[#ef000014] text-[#EF0000]'
                )}
              >
                {isOpened && T_PROFILE('opened')}
                {!isOpened && isOpeningSoon && T_PROFILE('opening_soon')}
                {!isOpened && !isOpeningSoon && T_PROFILE('currently_closed')}
              </span>
            )}

            <div className="discount-row mt-[24px] flex flex-col space-y-2 uppercase sm:flex-grow sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
              {(restaurant.latest_discount_code ||
                restaurant.aff_commission_formatted) && (
                <>
                  {restaurant.latest_discount_code && (
                    <div className="inline-flex items-center justify-center  space-x-1 rounded-3xl border-2 border-pink-200 bg-pink-50 px-[clamp(0.5rem,1vw,1.25rem)] py-2 text-[clamp(0.68rem,1vw,0.75rem)] text-pink-500">
                      <span className="font-medium leading-[14px]">
                        {/* {restaurant.latest_discount_code.cashback} */}
                        {restaurant.latest_discount_code.cashback}{' '}
                        {T_COMMON('common.discount_code')}
                      </span>
                      {/* <CheckIcon className="h-3.5 stroke-[3]" /> */}
                    </div>
                  )}
                  {restaurant.aff_commission_formatted && (
                    <div className="inline-flex items-center justify-center rounded-3xl border-2 border-green-100 bg-green-50 px-[clamp(0.5rem,1vw,2rem)] py-2 text-[clamp(0.68rem,1vw,0.75rem)]">
                      <span className="font-medium leading-[14px] text-green-500">
                        {restaurant.aff_commission_formatted}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* {type && type === 'pickup' && restaurant.allows_delivery && (
              <span
                className={`ml-auto inline-block rounded-full border border-[rgba(0,127,95,0.30)] bg-[linear-gradient(0deg,_rgba(36,192,48,0.08)_0%,_rgba(36,192,48,0.08)_100%)] px-[10px] py-[6px] text-xs text-[#007F5F] ${
                  rating.counts.all > 0 ? '' : 'self-end'
                }`}
              >
                {T_SEARCH('delivery_available')}
              </span>
            )} */}
            </div>
          </div>
        </Link>
      </motion.div>
      <FavoriteLoginModal
        isOpen={showLoginModal}
        setIsOpen={setShowLoginModal}
        redirectToLogin={redirectToLogin}
      />
    </>
  );
};
export default SearchCard;
